import { Modul_Kampagnenbox } from "@/wp/types/acf-module-types.ts";
import { useEffect, useState } from "react";
import { UserType } from "@/types/baseTypes.ts";
import { CustomLink } from "@/components/Global/CustomLink.tsx";
import { MediaItem } from "@/components/Global/MediaItem/MediaItem.tsx";
import { Section } from "@/components/Global/Section/Section.tsx";
import styles from "./CampaignBanner.module.scss";
import sanitize from "sanitize-html";
import { useRouter } from "next/router";
import {
  CampaignWithId,
  getCampaignDataByCampaignId,
  getCampaignDataByCategoryIds,
} from "@/utils/campaignUtils.ts";
import VidstackPlayer from "../Global/Video/VidstackPlayer";
import { Post } from "@/wp/types/client";
import { getVideoData } from "@/utils/getVideoData";

type CampaignBannerProps = {
  data: Modul_Kampagnenbox;
  user: UserType | undefined | null;
};

export const CampaignBanner = ({ data, user }: CampaignBannerProps) => {
  const { asPath } = useRouter();
  const {
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
  } = data.layout;
  const { type, categories, campaigns, size } = data.campaign_box;
  const [campaignData, setCampaignData] = useState<CampaignWithId | null>(null);
  const [adHitId, setAdHitId] = useState(null);
  const isRecipePage =
    asPath.includes("/rezepte/") || asPath.includes("/recettes");
  const inView = false;

  useEffect(() => {
    // TODO check functionallity when service api is reday
    if (campaignData) {
      fetch("/service-api/adhit", {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pageURL: window?.location.toString(),
          didImageLoad: false,
          wasClicked: false,
          format: size,
          wpCampaignTitle: campaignData.headline,
          wpCampaignId: campaignData.id,
          wpCustomer: campaignData.customer_name,
        }),
      })
        .then((response) => response.json())
        .then((data) => setAdHitId(data.id));
    }
    // Trigger only when campaignData changes (not on mount)
  }, [campaignData, size]);

  useEffect(() => {
    if (
      process.env.ENABLE_CAMPAIGN_BANNER &&
      process.env.ENABLE_CAMPAIGN_BANNER === "true"
    ) {
      if (type === "campaign" && (campaigns?.length ?? 0) > 0) {
        const ids = Array.isArray(campaigns)
          ? campaigns.map((x) => x.ID.toString()) // Map posts to id
          : typeof campaigns === "string"
            ? [campaigns] // if one id is provided
            : []; // if nothing is provided
        getCampaignDataByCampaignId(ids, size).then((campaign) =>
          setCampaignData(campaign),
        );
      } else if (categories?.length > 0) {
        getCampaignDataByCategoryIds(categories, user, size, isRecipePage).then(
          (campaign) => {
            setCampaignData(campaign);
          },
        );
      } else {
        getCampaignDataByCategoryIds(["all"], user, size, isRecipePage).then(
          (campaign) => {
            setCampaignData(campaign);
          },
        );
      }
    }
  }, [campaigns, categories, isRecipePage, size, type, user]);

  useEffect(() => {
    if (
      adHitId &&
      inView &&
      process.env.ENABLE_CAMPAIGN_BANNER &&
      process.env.ENABLE_CAMPAIGN_BANNER === "true"
    ) {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`/service-api/adhit/${adHitId}/view`);
      } else {
        fetch(`/service-api/adhit/${adHitId}/view`, {
          method: "POST",
          keepalive: true,
        }).catch((error) => {
          console.error(error);
        });
      }
    }
    // ? Only need to update when inView changes to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const getMedia = () => {
    switch (size) {
      case "small":
        return campaignData?.media.media_small;
      case "medium":
        return campaignData?.media.media_medium;
      case "big":
        return campaignData?.media.media_big;
      default:
        return null;
    }
  };

  const media = campaignData?.media;
  const link =
    typeof campaignData?.link == "object" ? campaignData?.link : null;

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  const router = useRouter();
  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const ContentJSX =
    size === "video" && videoData ? (
      // <video
      //   autoPlay
      //   muted
      //   playsInline
      //   loop
      //   className={`video`}
      //   poster={media?.video_poster ? media.video_poster.url : undefined}
      // >
      //   <source src={media?.video.url} type="video/mp4" />
      // </video>
      <VidstackPlayer
        video={videoData.acf.header_videos}
        autoplay
        muted
        singlePageUrl={videoData.link}
      />
    ) : (
      <>
        {getMedia()?.image_desktop && (
          <MediaItem
            sizes={"100%"}
            image_desktop={getMedia()!.image_desktop}
            image_tablet={getMedia()?.image_tablet}
            image_mobile={getMedia()?.image_mobile}
            className={`ml-0`}
            objectFit={"contain"}
          />
        )}
      </>
    );

  return (
    <>
      {campaignData && (
        <Section
          id={data.layout.id}
          className={`${styles["campaign-banner"]} ${section_margin_class} ${section_padding_class} is-clipped`}
          disableColumns={true}
        >
          <div
            className={`${styles["banner"]} columns ${styles[`format-${size}`]}`}
          >
            <div className={"column is-12 is-10-widescreen"}>
              {campaignData.headline && (
                <h2
                  className={`${headline_text_align_class}`}
                  dangerouslySetInnerHTML={{
                    __html: sanitize(campaignData.headline),
                  }}
                />
              )}
              {/*
              {true && (
                <div className={`${styles["ad-marker"]}`}>
                  <span className={`${styles["line"]}`} />
                  <span className={`${styles["label"]}`}>Anzeige</span>
                </div>
              )}
*/}
              {campaignData.content && (
                <span
                  className={`${styles["content"]} column is-12`}
                  dangerouslySetInnerHTML={{
                    __html: sanitize(campaignData.content),
                  }}
                />
              )}

              {ContentJSX}

              <CustomLink
                href={link?.url || ""}
                target={link?.target || ""}
                title={link?.title}
              >
                <em />
              </CustomLink>
            </div>
          </div>
        </Section>
      )}
    </>
  );
};
